/*
 *  Author: DL
 *  Date: 2020-12-06 14:18:36
 *  Email: dunaliang@fengmap.com
 *  LastEditTime: 2020-12-21 12:16:36
 *  LastEditors: DL
 *  Description: 路径规范
 *  Copyright: Copyright 2014 - 2020, FengMap, Ltd. All rights reserved.
 */

import React, { useCallback, useState, useRef, useEffect } from 'react';
import { connect } from 'dva';
import classnames from 'classnames';
import router from 'umi/router';
import { Button, Icon, Alert } from 'ui';
import SearchNavigation from '@/components/SearchNavigation';
import ZoomControl from '@/components/ZoomControl';
import FloorControl from '@/components/FloorControl';
import ViewModeControl from '@/components/ViewModeControl';
import LayerModeControl from '@/components/LayerModeControl';
import LocationControl from '@/components/LocationControl';
import FmapProvider from '@/components/FmapProvider';
import settingConfig from 'config/setting';
import { isEmpty } from 'utils/tools';
import styles from './index.less';

const { minOffset, maxOffset } = settingConfig;

function Scheme(props) {
    const { dispatch } = props;
    const { fmap, listGroups, focusGroupId, visibleModel } = props;
    const { coords, current } = props.map;

    const [desc, setDesc] = useState('');
    const [status, setStatus] = useState(props.location.query.status ? props.location.query.status : '');
    const [simulateStatus, setSimulateStatus] = useState('start');
    const [icon, setIcon] = useState('iconzhixing');
    const [title, setTitle] = useState('开始导航');
    const [subhead, setSubhead] = useState('');
    const [sound, setSound] = useState(true);
    const [timer, setTimer] = useState(new Date().valueOf());
    const [visible, setVisible] = useState(false);
    const globalRef = useRef({ navigation: null });

    const updateTitle = useCallback(title => setTitle(title), []);

    const updateSubhead = useCallback(remain => {
        const time = remain / 70;

        const m = parseInt(time);
        const s = Math.floor((time % 1) * 60);

        let prompt = `剩余${remain.toFixed(1)}米，步行${m}分${s}秒`;

        if (remain < 1) {
            remain = 0;
            prompt = '到达终点';
        }

        setSubhead(prompt);
    }, []);

    const updateIcon = useCallback(direction => {
        let icon = 'iconzhixing';

        switch (direction) {
            case '左':
                icon = 'iconxiangzuo';
                break;
            case '右':
                icon = 'iconxiangyou';
                break;
            case '左前':
                icon = 'iconzuoqianfang';
                break;
            case '右前':
                icon = 'iconyouxianfang';
                break;
            default:
                icon = 'iconzhixing';
        }

        setIcon(icon);
    }, []);

    const getInitSubheadTitle = useCallback(navigation => {
        const naviDistance = navigation.naviDistance;

        const time = naviDistance / 70;
        const m = parseInt(time);
        const s = Math.floor((time % 1) * 60);

        return `全程约 ${naviDistance.toFixed(1)} 米 步行预计用时 ${m} 分 ${s} 秒`;
    }, []);

    const createNaviSimulate = useCallback(
        (navigation, coords, fmap) => {
            if (isEmpty(coords)) return;

            fmap.createNaviSimulate(coords);

            const title = getInitSubheadTitle(navigation);

            setSubhead(title);
        },
        [setSubhead, getInitSubheadTitle],
    );

    // 模拟 walking
    const nvaiWalking = useCallback(
        data => {
            const { naviDescriptions, naviDescriptionsData } = globalRef.current;

            const { index, remain } = data;

            if (index === -1) return;
            globalRef.current.index = index;

            const descriptions = naviDescriptions[index];
            const descriptionsData = naviDescriptionsData[index];
            const direction = descriptionsData ? descriptionsData.endDirection : '';
            updateTitle(descriptions);
            updateSubhead(remain);
            updateIcon(direction);
        },
        [updateIcon, updateSubhead, updateTitle],
    );

    // 真实 walking
    const realNvaiWalking = useCallback(
        data => {
            const { naviDescriptions, naviDescriptionsData, navigation } = globalRef.current;
            const { index, remain } = data;
            if (index === -1) return;
            globalRef.current.index = index;

            const { x, y, groupID } = data.point;

            if (fmap.map.focusGroupId !== groupID) {
                //切换楼层；
                fmap.changeFloor(groupID);
            }

            fmap.map.moveTo({
                x,
                y,
                groupID,
                callback: () => {
                },
            });

            fmap.updateNaviLocationMarker(data.point);

            if (data.distance > minOffset) {
                fmap.visibleUpdateNaviLocationMarker(false);
                fmap.visibleLocationMarker(true);
            } else {
                fmap.visibleUpdateNaviLocationMarker(true);
                fmap.visibleLocationMarker(false);
            }

            if (data.distance > maxOffset) {
                navigation.clearAll();
                const { start } = coords;
                fmap.visibleLocationMarker(true);
                const model = { ...start, ...globalRef.current.current };
                navigation.off('walking', realNvaiWalking);
                dispatch({ type: 'map/updateCoordField', payload: { field: 'start', model } });
            }

            let descriptions = naviDescriptions[index];
            let descriptionsData = naviDescriptionsData[index];
            let direction = descriptionsData.endDirection;

            if (Math.trunc(data.remain) <= 3) {
                descriptions = '导航结束';
                direction = '目的地已在您附近';
                fmap.visibleUpdateNaviLocationMarker(false);
                fmap.visibleLocationMarker(true);
                navigation.off('walking', realNvaiWalking);
                setStatus('');
            }

            updateTitle(descriptions);
            updateSubhead(remain);
            updateIcon(direction);
        },
        [fmap, updateIcon, updateSubhead, updateTitle, coords, dispatch],
    );

    // 导航 完成事件
    const naviComplete = useCallback(() => {
        const navigation = fmap.navigation;
        setStatus('');
        const title = getInitSubheadTitle(navigation);
        setTitle('开始导航');
        setSubhead(title);
        navigation.clearLocationMarker();
        fmap.visibleLocationMarker(true);
        globalRef.current.ttsTimer = null;
    }, [fmap, getInitSubheadTitle]);

    // 导航 切换楼层
    const naviCrossGroup = useCallback(groupId => dispatch({ type: 'map/updateFocusGroupId', payload: { focusGroupId: groupId } }), [dispatch]);

    const handleBackClick = useCallback(() => {
        globalRef.current.navigation.clearAll();
        dispatch({ type: 'map/updateCoords', payload: { coords: {} } });
        router.push('/home');
    }, [dispatch]);

    const handleInputToggle = useCallback(() => {
        const { start, end } = coords;
        if (start.name === '我的位置') {
            setVisible(true);
        } else {
            dispatch({ type: 'map/updateCoords', payload: { coords: { start: { ...end }, end: { ...start } } } });
        }
    }, [coords, dispatch]);

    // 模拟导航
    const handleSimulateClick = useCallback(() => {
        window.tts('开始导航');
        if (globalRef.current.ttsTimer) {
            clearInterval(globalRef.current.ttsTimer);
        }

        globalRef.current.index = 0;

        const ttsTimer = setInterval(() => {
            const { index = -1, naviDescriptions } = globalRef.current;

            if (naviDescriptions.length - 1 === index) {
                clearInterval(ttsTimer);
            } else {
                if (index > -1) {
                    const title = naviDescriptions[index];

                    if (title !== globalRef.current.title) {
                        window.tts(title, globalRef.current.sound);
                    }

                    globalRef.current.title = title;
                }
            }
        }, 500);
        globalRef.current.ttsTimer = ttsTimer;

        setStatus('simulate');
        const navigation = fmap.navigation;
        fmap.visibleLocationMarker(false);
        navigation.on('walking', nvaiWalking);
        navigation.simulate();
    }, [fmap, nvaiWalking]);

    const handleInputClick = useCallback(() => {
        globalRef.current.navigation.clearAll();
        router.push('/route');
    }, []);

    // 真实 导航
    const handleNaviClick = useCallback(() => {
        window.tts('开始导航');
        if (globalRef.current.ttsTimer) {
            clearInterval(globalRef.current.ttsTimer);
        }
        const ttsTimer = setInterval(() => {
            const { index = -1, naviDescriptions } = globalRef.current;

            if (naviDescriptions.length - 1 === index) {
                clearInterval(ttsTimer);
            } else {
                if (index > -1) {
                    const title = naviDescriptions[index];

                    if (title !== globalRef.current.title) {
                        window.tts(title, globalRef.current.sound);
                    }

                    globalRef.current.title = title;
                }
            }
        }, 500);
        globalRef.current.ttsTimer = ttsTimer;

        setStatus('real');
    }, []);

    // 声音 开关
    const handleSoundClick = useCallback(() => {
        let flag = !sound;
        setSound(flag);
        globalRef.current.sound = flag;
    }, [sound]);

    // 模拟 暂停
    const handlePauseClick = useCallback(() => {
        clearInterval(globalRef.current.ttsTimer);
        let status = '';
        const navigation = globalRef.current.navigation;
        if (simulateStatus === 'start') {
            navigation.pause();
            status = 'pause';
        } else {
            navigation.resume();
            status = 'start';
        }
        setSimulateStatus(status);
    }, [simulateStatus]);

    // 模拟 退出
    const handleQuitSimulateClick = useCallback(() => {
        clearInterval(globalRef.current.ttsTimer);
        const navigation = fmap.navigation;
        navigation.stop();
        navigation.clearLocationMarker();
        const title = getInitSubheadTitle(navigation);
        setStatus('');
        setIcon('iconzhixing');
        setTitle('开始导航');
        setSubhead(title);
        fmap.visibleLocationMarker(true);
    }, [fmap, getInitSubheadTitle]);

    // 真实 退出
    const handleQuitRealClick = useCallback(() => {
        setStatus('');
        const navigation = fmap.navigation;
        navigation.off('walking', realNvaiWalking);
        const title = getInitSubheadTitle(fmap.navigation);
        setIcon('iconzhixing');
        setTitle('开始导航');
        setSubhead(title);
        fmap.visibleUpdateNaviLocationMarker(false);
        fmap.visibleLocationMarker(true);
    }, [fmap, getInitSubheadTitle, realNvaiWalking]);

    useEffect(() => {
        const intervalTimer = setInterval(() => {
            if (fmap && fmap.loadCompleteStatus) {
                clearInterval(intervalTimer);

                if (isEmpty(coords)) {
                    router.push('/home');
                    return;
                }

                // console.log('初始化');

                fmap.createNaviSimulate(coords);

                // const navigation = fmap.navigation;

                // const title = getInitSubheadTitle(navigation);
                // setDesc(title);

                // globalRef.current.navigation = navigation;
            }
        }, 500);

        return () => clearInterval(intervalTimer);
    }, [coords, fmap, setDesc, getInitSubheadTitle]);

    useEffect(() => {
        const intervalTimer = setInterval(() => {
            if (fmap && fmap.loadCompleteStatus) {
                clearInterval(intervalTimer);

                const navigation = fmap.navigation;
                globalRef.current.navigation = navigation;
                createNaviSimulate(navigation, coords, fmap);

                // 临时
                const title = getInitSubheadTitle(navigation);
                setDesc(title);

                setTimer(new Date().valueOf());

                globalRef.current.naviDescriptions = navigation.naviDescriptions;
                globalRef.current.naviDescriptionsData = navigation.naviDescriptionsData;

                navigation.on('complete', naviComplete);
                navigation.on('crossGroup', naviCrossGroup);

                globalRef.current.navigation = navigation;

                return () => {
                    navigation.off('complete', naviComplete);
                    navigation.off('crossGroup', naviCrossGroup);
                };
            }
        }, 500);
    }, [fmap, coords, createNaviSimulate, naviComplete, naviCrossGroup, getInitSubheadTitle]);

    // 初始化真实定位导航
    useEffect(() => {
        const intervalTimer = setInterval(() => {
            if (fmap && fmap.loadCompleteStatus) {
                clearInterval(intervalTimer);

                const navigation = fmap.navigation;
                if (status === 'real') {
                    navigation.on('walking', realNvaiWalking);

                    return () => navigation.off('walking', realNvaiWalking);
                }
            }
        }, 500);
    }, [fmap, status, realNvaiWalking]);

    // 实时定位导航
    useEffect(() => {
        if (status === 'real') {
            const { x, y, groupID } = current;
            fmap.navigation.locate({ x, y, groupID });
            globalRef.current.current = { x, y, groupID };
        }
    }, [fmap, status, current]);

    const startDefaultValue = coords.start && coords.start.name;
    const endDefaultValue = coords.end && coords.end.name;

    return (
        <FmapProvider value={{ fmap, listGroups, focusGroupId, current, visibleModel }}>
            <Alert message="我的位置不能作为终点" visible={visible} onClose={() => setVisible(false)} />
            {status === '' ? (
                <div className={styles.searchWrapper}>
                    <SearchNavigation key={timer} startDefaultValue={startDefaultValue} endDefaultValue={endDefaultValue} onInputClick={handleInputClick} onBackClick={handleBackClick} onInputToggle={handleInputToggle} />
                </div>
            ) : (
                <MessageWrapper title={title} subhead={subhead} icon={icon} />
            )}
            {status === '' ? (
                <div className={styles.controlWrapper}>
                    <LocationControl />
                    <LayerModeControl />
                    <ViewModeControl />
                    <FloorControl />
                    <ZoomControl />
                </div>
            ) : (
                <div className={styles.controlWrapper2}>
                    <Button onClick={handleSoundClick}>
                        <Icon className={sound ? 'iconlb01' : 'iconlb-gb01'} />
                    </Button>
                </div>
            )}
            {status === '' ? (
                <div className={styles.infoWrapper}>
                    <div className={styles.content}>
                        <span>{desc}</span>
                        <Button className={styles.close} onClick={handleBackClick}>
                            <Icon className="icongb002" />
                        </Button>
                    </div>
                    <div className={styles.naviBttn}>
                        <Button onClick={handleNaviClick}>导航</Button>
                        <Button type="primary" onClick={handleSimulateClick}>
                            模拟导航
                        </Button>
                    </div>
                </div>
            ) : (
                <FooterWrapper simulateStatus={simulateStatus} status={status} onPauseClick={handlePauseClick} onQuitSimulateClick={handleQuitSimulateClick} onQuitRealClick={handleQuitRealClick} />
            )}
        </FmapProvider>
    );
}

export default connect(({ map }) => ({ map }))(Scheme);

function MessageWrapper(props) {
    const { title, subhead, icon } = props;
    return (
        <div className={styles.messageWrapper}>
            <div className={styles.message}>
                <Icon className={classnames([icon, styles.directionIcon])} />
                <div className={styles.content}>
                    <p className={styles.title}>{title}</p>
                    <p className={styles.subhead}>{subhead}</p>
                </div>
            </div>
        </div>
    );
}

function FooterWrapper(props) {
    const { status, simulateStatus } = props;
    const { onQuitRealClick = () => {}, onPauseClick = () => {}, onQuitSimulateClick = () => {} } = props;

    return (
        <div className={styles.bttnWrapper}>
            {status === 'simulate' ? (
                <>
                    <Button onClick={onPauseClick}>{`${simulateStatus === 'start' ? '暂停' : '开始'}`}模拟</Button>
                    <Button type="primary" onClick={onQuitSimulateClick}>
                        退出模拟
                    </Button>
                </>
            ) : (
                <Button type="primary" onClick={onQuitRealClick}>
                    退出导航
                </Button>
            )}
        </div>
    );
}
